import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { ROLE_OPTIONS, EXCLUSIVE_ROLES } from "@/lib/roles";
import { capitalizeName } from "@/lib/validators";

const EMPTY = { first_name: "", last_name: "", email: "", password: "", roles: ["valuatore"], organization_id: "", partner_org_ids: [] };

function toggleRole(roles, r) {
  if (roles.includes(r)) return roles.filter((x) => x !== r);
  if (EXCLUSIVE_ROLES.includes(r)) return [r];
  return [...roles.filter((x) => !EXCLUSIVE_ROLES.includes(x)), r];
}

export default function UserFormDialog({ open, editing, orgs, onClose, onSave }) {
  const [form, setForm] = useState(EMPTY);
  useEffect(() => {
    if (!open) return;
    setForm(editing ? {
      first_name: editing.first_name || (editing.name || "").split(" ")[0] || "",
      last_name: editing.last_name || (editing.name || "").split(" ").slice(1).join(" "),
      email: editing.email || "", password: "", roles: editing.roles || [editing.role],
      organization_id: editing.organization_id || "", partner_org_ids: editing.partner_org_ids || [],
    } : EMPTY);
  }, [open, editing]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const togglePartnerOrg = (id) => set("partner_org_ids", form.partner_org_ids.includes(id)
    ? form.partner_org_ids.filter((x) => x !== id) : [...form.partner_org_ids, id]);

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto" data-testid="user-form-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl">{editing ? "Modifica utente" : "Nuovo utente"}</DialogTitle>
        </DialogHeader>
        {editing && !editing.is_nominative && (
          <div className="rounded-lg bg-amber-50 border border-amber-200 text-amber-800 text-xs px-3 py-2" data-testid="user-form-nominative-warning">
            Account non nominativo: inserisci nome e cognome della persona titolare.
          </div>
        )}
        <div className="grid grid-cols-2 gap-4 mt-2">
          <Row label="Nome *"><Input value={form.first_name} onChange={(e) => set("first_name", e.target.value)} onBlur={() => set("first_name", capitalizeName(form.first_name))} data-testid="user-first-name-input" className="h-10" /></Row>
          <Row label="Cognome *"><Input value={form.last_name} onChange={(e) => set("last_name", e.target.value)} onBlur={() => set("last_name", capitalizeName(form.last_name))} data-testid="user-last-name-input" className="h-10" /></Row>
          <div className="col-span-2"><Row label="Email personale *"><Input type="email" value={form.email} onChange={(e) => set("email", e.target.value)} data-testid="user-email-input" className="h-10" /></Row></div>
          <div className="col-span-2">
            <Row label={editing ? "Nuova password (vuoto = invariata; l'utente dovrà cambiarla)" : "Password iniziale * (l'utente dovrà cambiarla al primo accesso)"}>
              <Input type="password" value={form.password} onChange={(e) => set("password", e.target.value)} data-testid="user-password-input" className="h-10" />
            </Row>
          </div>
        </div>
        <Row label="Ruoli">
          <div className="grid sm:grid-cols-2 gap-2" data-testid="user-roles-checkboxes">
            {ROLE_OPTIONS.map((r) => (
              <label key={r.value} className="flex items-start gap-2 rounded-lg border border-slate-200 p-2 cursor-pointer hover:bg-slate-50">
                <Checkbox checked={form.roles.includes(r.value)} onCheckedChange={() => set("roles", toggleRole(form.roles, r.value))}
                          data-testid={`user-role-${r.value}`} className="mt-0.5" />
                <span><span className="text-sm font-medium text-slate-800">{r.label}</span><span className="block text-[11px] text-slate-500">{r.hint}</span></span>
              </label>
            ))}
          </div>
          <p className="text-[11px] text-slate-400 mt-1">Cliente e Partner non sono combinabili con altri ruoli.</p>
        </Row>
        {form.roles.includes("cliente") && (
          <Row label="Azienda collegata *">
            <Select value={form.organization_id} onValueChange={(v) => set("organization_id", v)}>
              <SelectTrigger data-testid="user-org-select" className="h-10"><SelectValue placeholder="Scegli azienda..." /></SelectTrigger>
              <SelectContent>{orgs.map((o) => <SelectItem key={o.id} value={o.id}>{o.ragione_sociale}</SelectItem>)}</SelectContent>
            </Select>
          </Row>
        )}
        {form.roles.includes("partner") && (
          <Row label={`Aziende dell'accordo (${form.partner_org_ids.length})`}>
            <div className="max-h-48 overflow-y-auto border border-slate-200 rounded-lg p-2 space-y-1" data-testid="user-partner-orgs">
              {orgs.map((o) => (
                <label key={o.id} className="flex items-center gap-2 text-sm cursor-pointer">
                  <Checkbox checked={form.partner_org_ids.includes(o.id)} onCheckedChange={() => togglePartnerOrg(o.id)} data-testid={`user-partner-org-${o.id}`} />
                  {o.ragione_sociale}
                </label>
              ))}
            </div>
          </Row>
        )}
        <DialogFooter className="mt-2">
          <Button variant="ghost" onClick={onClose}>Annulla</Button>
          <Button onClick={() => onSave(form)} data-testid="save-user-button" className="bg-viva-500 hover:bg-viva-600 text-white">
            {editing ? "Salva" : "Crea"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Row({ label, children }) {
  return (
    <div className="mt-3">
      <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">{label}</Label>
      <div className="mt-1.5">{children}</div>
    </div>
  );
}
